import { z } from 'zod';
import { optionalPattern } from './zodPatterns';

// Field-level rules only — deliberately NO cross-field `.refine`/`.superRefine`
// (the shared @components/Form runs the legacy zod adapter; see
// location.schema.ts for the full story). Required fields mirror
// Api::OrganisationsController#create's boundary rules (GRAIN-2237): name only.
//
// The form carries the Papercut round-trip fields (GRAIN-1797): name, industry
// (Sector), tier (Tier — GRAIN-2748, replaces the mislabelled size_category),
// website (Domain), payment_terms_in_days — plus the grain-core-owned notes
// (GRAIN-1809). tier allows '' so the "no selection" (N/A) state validates.

// Mirrors Organisation#normalize_website + #validate_website_format: a bare
// hostname (e.g. "acme.com") is accepted — the backend prepends https:// on
// save — so the scheme is optional here to match the "Domain" field label.
const WEBSITE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:[\/?#]\S*)?$/;

// Payment terms is net days — a non-negative whole number, blank = "no terms".
// Held as a string in form state; converted to number | null at the payload
// boundary. Capped at 4 digits here to reject absurd input immediately; the
// precise ceiling (Organisation::MAX_PAYMENT_TERMS_IN_DAYS = 3650) is enforced
// server-side and surfaces as an inline field error. `\d{0,4}` avoids a
// `.refine` (which dead-locks the legacy adapter).
// Exported because the edit-order form carries the same organisation field and
// must accept exactly what this form accepts.
export const PAYMENT_TERMS_REGEX = /^\d{0,4}$/;

export type OrganisationValidationMessages = {
  nameRequired?: string;
  websiteInvalid?: string;
  paymentTermsInvalid?: string;
};

const DEFAULT_VALIDATION_MESSAGES: Required<OrganisationValidationMessages> = {
  nameRequired: 'Name is required',
  websiteInvalid: 'Enter a valid domain',
  paymentTermsInvalid: 'Enter a whole number of days',
};

export function organisationFormSchemaWithMessages(messages?: OrganisationValidationMessages) {
  const m = { ...DEFAULT_VALIDATION_MESSAGES, ...messages };

  return z.object({
    name: z.string().min(1, m.nameRequired),
    tier: z.string(),
    industry: z.string(),
    website: z.string().regex(optionalPattern(WEBSITE_REGEX), m.websiteInvalid),
    payment_terms_in_days: z.string().regex(PAYMENT_TERMS_REGEX, m.paymentTermsInvalid),
    notes: z.string().optional(),
  });
}

// Static schema with default messages (for tests / non-i18n callers).
export const organisationFormSchema = organisationFormSchemaWithMessages();

export type OrganisationFormData = z.infer<typeof organisationFormSchema>;
